import {
  Component,
  HostBinding,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef
} from '@angular/core';
import { NavItem } from './nav-item';
import { Router, RouterModule } from '@angular/router';
import { isNavUrlActive } from '@auth-suite-services/services';
import { NavService } from '../../../../../services/nav.service';
import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from '../../../../../material.module';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-nav-item',
  standalone: true,
  imports: [TablerIconsModule, TranslateModule, MaterialModule, CommonModule, RouterModule],
  templateUrl: './nav-item.component.html',
  styleUrls: [],
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4,0.0,0.2,1)')
      )
    ])
  ]
})
export class AppNavItemComponent implements OnInit, OnChanges, OnDestroy {
  @Output() toggleMobileLink: any = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();

  expanded: any = false;
  activeMenu = false;
  disabled: any = false;
  twoLines: any = false;
  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @Input() item: NavItem | any;
  @Input() depth: any;
  private urlSub?: Subscription;

  constructor(
    public navService: NavService,
    public router: Router,
    private cdr: ChangeDetectorRef
  ) {
    if (this.depth === undefined) {
      this.depth = 0;
    }
  }

  ngOnInit(): void {
    this.urlSub = this.navService.currentUrl.subscribe((url: string) => {
      this.syncActiveState(url || this.router.url);
    });
    this.syncActiveState(this.router.url);
  }

  ngOnChanges() {
    this.syncActiveState(this.router.url);
  }

  ngOnDestroy(): void {
    this.urlSub?.unsubscribe();
  }

  // Keep browse highlighted on sibling edit/detail routes 28/10/2026 DW
  private isItemActive(item: NavItem | undefined, path: string): boolean {
    if (!item || !path) {
      return false;
    }
    if (isNavUrlActive(path, item.route)) {
      return true;
    }
    return (item.activeMatchRoutes ?? []).some((r) => isNavUrlActive(path, r));
  }

  private syncActiveState(url: string): void {
    const path = (url || '').split('?')[0];
    this.activeMenu = this.isItemActive(this.item, path);
    if (this.item?.children?.length && path) {
      const childActive = this.item.children.some((child: NavItem) =>
        this.isItemActive(child, path)
      );
      if (childActive) {
        this.expanded = true;
        this.ariaExpanded = true;
      }
    }
    this.cdr.detectChanges();
  }

  onItemSelected(item: NavItem) {
    if (item.children && item.children.length) {
      this.expanded = !this.expanded;
      this.ariaExpanded = this.expanded;
    }
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    if (!this.expanded) {
      if (window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      if (this.expanded && window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  // Stable kebab-case id for Selenium nav targets 19/10/2026 DW
  navItemAutomationId(suffix = ''): string {
    const raw = (this.item?.route || this.item?.displayName || 'item')
      .toString()
      .replace(/^\/+/, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
    const base = `nav-${raw}`;
    return suffix ? `${base}-${suffix}-link` : `${base}-link`;
  }
}
